<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search + Add -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm search-input"
                  placeholder="&#xF002; Search AI categories..."
                  @keyup="onSearch()" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn btn-sm add-btn"
                  @click="showAddModal = true">
                  <i class="fa-solid fa-plus me-1"></i> Add AI Category
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">Name</th>
                     <th scope="col">Domain</th>
                     <th scope="col">Description</th>
                     <th scope="col" style="width: 80px;">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td colspan="4">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(cat, index) in aiCategories" :key="index" class="table-row-hover">
                     <td class="fw-bold text-dark">
                        <i class="fa-solid fa-robot me-2 text-cyan"></i>
                        {{ cat.name || '—' }}
                     </td>
                     <td>
                        <span class="badge bg-light text-secondary border font-monospace">{{ cat.domain }}</span>
                     </td>
                     <td class="text-muted">{{ cat.description || '—' }}</td>
                     <td>
                        <button type="button" class="ms-action-btn ms-action-btn--delete"
                           @click="confirmDelete(cat)">
                           <i class="fa-solid fa-xmark"></i>
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>

   <!-- Add Modal -->
   <div class="modal-backdrop-custom" v-if="showAddModal" @click.self="closeAddModal()">
      <div class="modal-content-custom">
         <button @click="closeAddModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-robot me-2 text-cyan"></i>
            Add AI Category
         </h5>
         <hr>
         <div class="modal-body-custom">
            <div class="mb-3">
               <label class="filter-label">Domain Name</label>
               <input type="text" class="form-control form-control-sm"
                  style="border-radius: 8px;"
                  placeholder="e.g. openai.com"
                  v-model="inputDomain" v-on:keyup.enter="addAiCategory()">
               <small class="text-muted mt-1 d-block">Base domain for this AI platform.</small>
            </div>
            
            <div class="mb-3">
               <label class="filter-label">Platform Name</label>
               <input type="text" class="form-control form-control-sm"
                  style="border-radius: 8px;"
                  placeholder="e.g. OpenAI"
                  v-model="inputName">
               <small class="text-muted mt-1 d-block">Display name of the platform.</small>
            </div>

            <div class="mb-3">
               <label class="filter-label">Description</label>
               <textarea class="form-control form-control-sm"
                  style="border-radius: 8px;"
                  placeholder="e.g. OpenAI Platform & API"
                  v-model="inputDescription" rows="2"></textarea>
               <small class="text-muted mt-1 d-block">Short description of this AI category.</small>
            </div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="closeAddModal()">Cancel</button>
            <button type="button" class="btn btn-sm text-light save-btn"
               @click="addAiCategory()">
               <i class="fa-solid fa-plus me-1"></i> Add
            </button>
         </div>
      </div>
   </div>

   <!-- Delete Confirm Modal -->
   <div class="modal-backdrop-custom" v-if="showDeleteModal" @click.self="showDeleteModal = false">
      <div class="modal-content-custom">
         <button @click="showDeleteModal = false" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-xmark me-2 text-danger"></i>
            Delete AI Category
         </h5>
         <hr>
         <div class="modal-body-custom">
            <p>Are you sure you want to remove this category from the AI master list?</p>
            <div class="site-display">{{ deleteCategoryName }} ({{ deleteCategoryDomain }})</div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="showDeleteModal = false">Cancel</button>
            <button type="button" class="btn btn-sm text-light btn-danger" style="border-radius: 8px;"
               @click="deleteAiCategory()">
               <i class="fa-solid fa-xmark me-1"></i> Delete
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {
   data() {
      return {
         showAddModal: false,
         showDeleteModal: false,
         deleteID: 0,
         deleteCategoryName: '',
         deleteCategoryDomain: '',
         search: "",
         aiCategories: [],
         inputDomain: "",
         inputName: "",
         inputDescription: "",
         debounceFn: null,
         paginationData: {},
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "ID: Newest", value: "id__desc" },
            { text: "ID: Oldest", value: "id__asc" },
            { text: "Domain: A-Z", value: "domain__asc" },
            { text: "Domain: Z-A", value: "domain__desc" },
            { text: "Name: A-Z", value: "name__asc" },
            { text: "Name: Z-A", value: "name__desc" },
         ],
         sortBy: "id__desc",
         sorting: { field: "id", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getAiCategories() },
      rowsPerPage() { this.pagination.page = 1; this.getAiCategories() },
      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getAiCategories()
      },
   },

   created() {
      this.debounceFn = debounce(() => this.getAiCategories(), 500)
      this.getAiCategories()
   },

   methods: {
      isValidDomain(domain) {
         const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
         return domainPattern.test(domain);
      },

      confirmDelete(cat) {
         this.deleteID = cat.id
         this.deleteCategoryName = cat.name || '—'
         this.deleteCategoryDomain = cat.domain
         this.showDeleteModal = true
      },

      closeAddModal() {
         this.showAddModal = false
         this.inputDomain = ""
         this.inputName = ""
         this.inputDescription = ""
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      async addAiCategory() {
         try {
            if (!this.inputDomain || !this.isValidDomain(this.inputDomain)) {
               this.$toast.error("Please provide a valid website domain.")
               return
            }
            if (!this.inputName.trim()) {
               this.$toast.error("Please provide a name for this AI platform.")
               return
            }
            const payload = {
               domain: this.inputDomain.trim().toLowerCase(),
               name: this.inputName.trim(),
               description: this.inputDescription.trim()
            }
            const resp = await this.$axios.post("admin-controls/create-ai-category", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getAiCategories()
            }
            this.closeAddModal()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to add AI category."))
            this.closeAddModal()
         }
      },

      async deleteAiCategory() {
         try {
            const resp = await this.$axios.delete("admin-controls/remove-ai-category/" + this.deleteID)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getAiCategories()
            }
            this.deleteID = 0
            this.deleteCategoryName = ''
            this.deleteCategoryDomain = ''
            this.showDeleteModal = false
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to delete AI category."))
            this.deleteID = 0
            this.showDeleteModal = false
         }
      },

      async getAiCategories() {
         let apiPath = "admin-controls/get-ai-categories?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&order_by=" + this.sorting.order
            apiPath += "&field=" + this.sorting.field
         }
         if (this.search !== "" && this.search !== null) {
            apiPath += "&search=" + encodeURIComponent(this.search)
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.ai_categories === "No Record Found")
                  this.aiCategories = []
               else {
                  this.aiCategories = response.data.ai_categories
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load AI categories."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.search-input {
   border-radius: 8px;
   height: 40px;
   font-size: 14px;
   font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free';
   font-weight: 900;
}

.add-btn {
   font-size: 14px;
   height: 40px;
   border-radius: 8px !important;
   padding: 0 1.5rem;
   white-space: nowrap;
   background: #00A0C1;
   color: #fff;
}
.add-btn:hover {
   background: #008ba8;
   color: #fff;
}

.save-btn {
   background: #00A0C1;
   border-radius: 8px;
}
.save-btn:hover {
   background: #008ba8;
}

.text-cyan {
   color: #00A0C1;
}

.table-row-hover:hover {
   background-color: #fcfdfe;
}

.site-display {
   font-family: monospace; font-size: 13px;
   background: #e6f7fa; padding: 8px 12px;
   border-radius: 8px; color: #008ba8;
   word-break: break-all;
}

/* Modal */
.modal-backdrop-custom {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background-color: rgba(0,0,0,0.5); display: flex;
   justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
   position: relative; background: #fff; padding: 2rem;
   border-radius: 14px; width: 100%; max-width: 480px;
   box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.modal-title-custom { font-weight: 700; font-size: 1.1rem; color: #435666; margin-bottom: 0; }
.modal-content-custom hr { margin: 0.75rem 0 1rem; border-color: #e9ecef; }
.close-button-custom {
   position: absolute; top: 0.75rem; right: 1rem;
   font-size: 1.8rem; font-weight: bold; line-height: 1;
   color: #adb5bd; background: none; border: none; cursor: pointer;
}
.close-button-custom:hover { color: #435666; }
.modal-body-custom { font-size: 14px; color: #435666; }
.filter-label {
   font-size: 11px; font-weight: 600; color: #6c757d;
   text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px;
   display: block;
}
.modal-footer-custom { margin-top: 1rem; display: flex; justify-content: flex-end; gap: 8px; }
.btn-modal-cancel { background: #e9ecef; border: none; border-radius: 8px; color: #435666; }
</style>
